import { clearNestedObjects } from '@utils/clearNestedObjects.ts';
import { directusClient } from '@src/api/DirectusClient.ts';
import { getParametersForHook } from '../utils/getParametersForHook';
import type { BenefitDto } from '@src/api/api.types.ts';
import type {
  JobDTO,
  JobDTOForUpdate,
  JobDTORelationsResolved,
} from '@funnellab/shared/src/dtos/JobDTO';
import type { JobDetails, JobForWizard, JobWizardDTO } from '../queries/job';

export class JobService {
  clearJobObjectForDirectus = (job: JobForWizard): JobForWizard => {
    return clearNestedObjects(
      job,
      ['location_id', 'organization', 'employerBrand'],
      ['locations'],
      [
        'idExternal',
        'nextRenewal',
        'monthlyCampaignBudget',
        'applicantManagementUrl',
        'notes',
        'owner',
        'creatives',
        'adText',
      ],
    ) as Partial<JobForWizard>;
  };

  jobWizardDtoToJobForWizardDto(jobWizardDto: JobWizardDTO): JobForWizard {
    return {
      ...jobWizardDto,
      userCampaignStartDate: jobWizardDto.userCampaignStartDate
        ? new Date(jobWizardDto.userCampaignStartDate)
        : null,
    };
  }

  async getJobForWizard(id: string): Promise<JobForWizard> {
    const jobWizardDto = await directusClient.getJobForWizzard(id);
    return this.jobWizardDtoToJobForWizardDto(jobWizardDto);
  }

  async getJobForDetailView(id: string): Promise<JobDetails> {
    return await directusClient.getJobForDetailView(id);
  }

  async createJob(job: JobForWizard): Promise<JobForWizard> {
    const copy = this.clearJobObjectForDirectus(job);
    const jobWizardDto = await directusClient.createJob(
      copy as Partial<
        JobDTO &
          Pick<
            JobDTORelationsResolved,
            'organization' | 'employerBrand' | 'owner'
          >
      >,
    );
    return this.jobWizardDtoToJobForWizardDto(jobWizardDto);
  }

  async deleteJob(id: string): Promise<void> {
    return directusClient.deleteJob(id);
  }

  async copyJob(id: string): Promise<JobDTO> {
    return directusClient.copyJob(id);
  }

  updateWizardJob = async (
    jobId: string,
    job: JobDTOForUpdate,
  ): Promise<JobForWizard> => {
    const jobWizardDto = await directusClient.updateJobForWizard(jobId, job);
    return this.jobWizardDtoToJobForWizardDto(jobWizardDto);
  };

  updateDetailsViewJob = async (
    jobId: string,
    job: JobDTOForUpdate,
  ): Promise<JobDetails> => {
    return directusClient.updateJobForDetailsView(jobId, job);
  };

  async createBenefit(benefit: Partial<BenefitDto>): Promise<BenefitDto> {
    // @ts-expect-error This will be removed soon
    return directusClient.createBenefit(benefit);
  }

  async getHook(
    jobId: string,
    currentJobState: Pick<
      JobForWizard,
      | 'targetCareerJumpers'
      | 'addressing'
      | 'targetJobTitles'
      | 'targetExperiences'
      | 'targetEducations'
      | 'targetSkills'
      | 'locations'
      | 'hook'
    >,
  ): Promise<string | null> {
    const paramsForHook = getParametersForHook(currentJobState);
    return (await directusClient.getHook(jobId, paramsForHook)).hook;
  }
}

const jobService = new JobService();
export default jobService;

// -------------------------------------------------------------------------
// This will be the next Generation JobService

// type JobContext = 'PostCreativeBuilder' | 'StoryCreativeBuilder';
// type Relation = 'postSceneFile' | 'storySceneFile';
// const relationContextMapping: Record<JobContext, Relation[]> = {
//   PostCreativeBuilder: ['postSceneFile'],
//   StoryCreativeBuilder: ['storySceneFile'],
// };
export type Job = {
  id: string;
  scenes: {
    post: null | string;
    story: null | string;
  };
};
type JobForUpdate = Partial<Omit<Job, 'scenes'>> & {
  scenes?: {
    post?: File | null;
    story?: File | null;
  };
};

const mapJobDTOToJob = (jobDTO: JobDTO): Job => {
  return {
    id: jobDTO.id,
    scenes: {
      post: jobDTO.scenes.post,
      story: jobDTO.scenes.story,
    },
  };
};
const mapJobToJobDTO = (
  _job: Partial<JobForUpdate>,
): Partial<JobDTOForUpdate> => {
  const dto: JobDTOForUpdate = {};
  return dto;
};

export const getJob = async (id: string) => {
  const jobDTO = await directusClient.getJob(id);

  return mapJobDTOToJob(jobDTO);
};

export const updateJob = async (id: string, data: JobForUpdate) => {
  const dtoData = mapJobToJobDTO(data);
  return directusClient.updateJob(id, dtoData);
};
